import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { Select } from '@/components/ui/Select';
import { UsersIcon } from '@/components/icons';
import { httpClient } from '@/lib/api';
import { useAccess, canDelegateRole, type Overview } from './access';

export const StaffCreateForm = ({ data, onClose }: { data: Overview; onClose: () => void }) => {
  const access = useAccess();
  const cache = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const roles = data.roles.filter((role) => canDelegateRole(access.data, role));
  const save = useMutation({
    mutationFn: () =>
      httpClient.post('/access/users', {
        firstName,
        lastName,
        email,
        password,
        roleId: roleId || null,
      }),
    onSuccess: async () => {
      setPassword('');
      await cache.invalidateQueries({ queryKey: ['access-administration'] });
      onClose();
    },
  });
  return (
    <div className="access-page access-form-page">
      <nav className="access-breadcrumb" aria-label="Fil d’Ariane">
        <Link to="/parametres/acces?onglet=users">Accès et équipes</Link>
        <span aria-hidden>›</span>
        <span aria-current="page">Nouvel utilisateur</span>
      </nav>
      <header className="access-heading access-detail-heading">
        <span className="access-heading-icon">
          <UsersIcon />
        </span>
        <div>
          <h1>Créer un utilisateur</h1>
          <p>Un compte interne pour accéder à la plateforme ubeefy.</p>
        </div>
      </header>
      <form
        id="staff-create"
        className="access-editor access-form-card"
        onSubmit={(event) => {
          event.preventDefault();
          if (!save.isPending) save.mutate();
        }}
      >
        {save.isError && (
          <p className="access-error" role="alert">
            {save.error.message}
          </p>
        )}
        <section className="access-form-section">
          <h2>Identité du collaborateur</h2>
          <div className="access-editor-identity">
            <TextField
              label="Prénom"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              required
              maxLength={80}
              autoComplete="given-name"
              disabled={save.isPending}
            />
            <TextField
              label="Nom"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              required
              maxLength={80}
              autoComplete="family-name"
              disabled={save.isPending}
            />
          </div>
        </section>
        <section className="access-form-section">
          <h2>Connexion</h2>
          <div className="access-editor-identity">
            <TextField
              label="Adresse e-mail"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              autoComplete="off"
              disabled={save.isPending}
            />
            <TextField
              label="Mot de passe initial"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              minLength={12}
              maxLength={128}
              pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{12,}"
              title="Au moins 12 caractères, une majuscule, une minuscule et un chiffre."
              autoComplete="new-password"
              disabled={save.isPending}
            />
          </div>
          <p className="access-hint">
            Le mot de passe doit contenir au moins 12 caractères, une majuscule, une minuscule et un
            chiffre.
          </p>
        </section>
        <section className="access-form-section">
          <h2>Rôle et accès</h2>
          <Select
            label="Rôle initial"
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
            placeholder="Aucun accès pour le moment"
            options={roles.map((role) => ({ value: role.id, label: role.name }))}
            disabled={!access.can('permissions', 'update') || save.isPending}
          />
          <p className="access-hint">
            Sans rôle, ce compte n’a accès à aucun module. L’attribution d’un rôle nécessite le
            droit de modification des permissions.
          </p>
        </section>
      </form>
      <div className="access-form-footer">
        <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
          Annuler
        </Button>
        <Button type="submit" form="staff-create" loading={save.isPending} loadingLabel="Création…">
          Créer l’utilisateur
        </Button>
      </div>
    </div>
  );
};
